import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Layout, message, Skeleton, Select } from 'antd';
import styled from '@emotion/styled';
import useSWR from 'swr';
import { TourTrashModal, TourTrashPageHeader, TourTrashTable } from '../../organisms/tour-trash';
import { getTourTrashListRepository, TourtrashResponse } from '../../../repository/mytm/tour-trash/tourTrashRepository';
import { API_URLS } from '../../../constants/urls/api-urls';
import { TourTrashStatusType, TourTrashType } from '../../../types/tourtrash';
import { appStyles } from '../../../styles';
import { MESSAGE_ERROR } from '../../../constants/message';
import { PAGE_SIZE_OPTIONS, PER_PAGE_DEFAULT } from '../../../constants/common';
import { IconUserProfile } from '../../../assets';
import SelectButtons from '../../atoms/select-buttons/SelectButtons';
import { useCountrySelection } from '../../../hooks/useCountrySelection';

const { Content } = Layout;

export const TourTrash = () => {
  const [loading] = useState(false);

  const [isOpenTourTrashModal, setIsOpenTourTrashModal] = useState(false);
  const [isDisabledDetailTab, setIsDisabledDetailTab] = useState(false);
  const [isDisabledUserTab, setIsDisabledUserTab] = useState(true);
  const [recordEdit, setRecordEdit] = useState<TourTrashType>();
  const [isHandleViewTourTrash, setIsHandleViewTourTrash] = useState<boolean>(false);
  const [isEditTourTrashActive, setIsEditTourTrashActive] = useState<boolean>(false);
  const [countrySelected, setCountrySelected] = useCountrySelection(null);
  const [displayStatus, setDisplayStatus] = useState<TourTrashStatusType>(TourTrashStatusType.DRAFT);
  const errorCount = useRef(0);

  const onClickBtnCreateTourTrash = useCallback(() => {
    setIsOpenTourTrashModal(true);
    setIsDisabledUserTab(true);
    setIsDisabledDetailTab(false);
    setRecordEdit(undefined);
    setIsHandleViewTourTrash(false);
    setIsEditTourTrashActive(false);
  }, []);

  const onCloseTourTrashModal = useCallback(() => {
    setIsOpenTourTrashModal(false);
  }, []);

  const [pageSize, setPageSize] = useState(PER_PAGE_DEFAULT);
  const [page, setPage] = useState(1);

  const onShowSizeChange = useCallback((current: number, size: number) => {
    setPage(current);
    setPageSize(size || PER_PAGE_DEFAULT);
  }, []);
  const handleChangePage = useCallback((pg: number, pgSize?: number) => {
    setPage(pg);
    setPageSize(pgSize || PER_PAGE_DEFAULT);
  }, []);
  const onHandleClickBtnEdit = useCallback((record: TourTrashType, editTourTrashActive?: boolean) => {
    setIsOpenTourTrashModal(true);
    setIsDisabledUserTab(true);
    setIsDisabledDetailTab(false);
    setRecordEdit(record);
    setIsEditTourTrashActive(!!editTourTrashActive);
    setIsHandleViewTourTrash(false);
  }, []);

  const onHandleViewTourtrash = useCallback((record: TourTrashType) => {
    setIsOpenTourTrashModal(true);
    setIsDisabledUserTab(false);
    setIsDisabledDetailTab(false);
    setRecordEdit(record);
    setIsEditTourTrashActive(false);
    setIsHandleViewTourTrash(true);
  }, []);

  const onUpdateEditingRecord = useCallback((record: TourTrashType) => {
    setRecordEdit(record);
  }, []);

  const [pageSizeDraft, setPageSizeDraft] = useState(PER_PAGE_DEFAULT);
  const [pageDraft, setPageDraft] = useState(1);
  const [isValidatingDraft, setValidatingDraft] = useState(false);
  const [dataDraft, setDataDraft] = useState<TourtrashResponse>();
  const fetchDataDraft = async () => {
    if (!countrySelected) return; // Don't fetch if country not selected

    try {
      setValidatingDraft(true);
      const data = await getTourTrashListRepository(API_URLS.TOUR_TRASH_LIST, {
        page: pageDraft,
        take: pageSizeDraft || PER_PAGE_DEFAULT,
        status: displayStatus,
        country: countrySelected,
      });
      setDataDraft(data);
      setValidatingDraft(false);
    } catch (error) {
      setDataDraft({
        total: 0,
        take: 15,
        page: 15,
        data: [],
      });
      setValidatingDraft(false);
    }
  };
  useEffect(() => {
    fetchDataDraft();
  }, [pageDraft, pageSizeDraft, displayStatus]);
  const onShowSizeChangeDraft = useCallback((current: number, size: number) => {
    setPageDraft(current);
    setPageSizeDraft(size || PER_PAGE_DEFAULT);
  }, []);
  const handleChangePageDraft = useCallback((pg: number, pgSize?: number) => {
    setPageDraft(pg);
    setPageSizeDraft(pgSize || PER_PAGE_DEFAULT);
  }, []);
  const { data, isValidating, mutate, error } = useSWR(
    countrySelected ? [API_URLS.TOUR_TRASH_LIST, page, pageSize, countrySelected] : null,
    (url: string, pageNumber: number, pageSizeNumber: number) =>
      getTourTrashListRepository(url, {
        page: pageNumber,
        take: pageSizeNumber || PER_PAGE_DEFAULT,
        country: countrySelected!,
      }),
    {
      revalidateOnFocus: false,
      refreshInterval: 600000,
    },
  );

  const refresh = () => {
    mutate();
    fetchDataDraft();
  };

  useEffect(() => {
    fetchDataDraft();
  }, [countrySelected]);

  const draftTourtrashList =
    dataDraft?.data.map((item, index) => ({
      ...item,
      index,
    })) || [];
  const tourTrashModal = useMemo(() => {
    if (!countrySelected) return null;

    return (
      <TourTrashModal
        isOpenModal={isOpenTourTrashModal}
        onClose={onCloseTourTrashModal}
        onRefresh={refresh}
        onUpdateEditingRecord={onUpdateEditingRecord}
        countrySelected={countrySelected}
        {...{
          isDisabledDetailTab,
          isDisabledUserTab,
          recordEdit,
          isHandleViewTourTrash,
          isEditTourTrashActive,
        }}
      />
    );
  }, [
    isOpenTourTrashModal,
    isDisabledDetailTab,
    isDisabledUserTab,
    onCloseTourTrashModal,
    recordEdit,
    mutate,
    onUpdateEditingRecord,
    isHandleViewTourTrash,
    isEditTourTrashActive,
    countrySelected,
  ]);

  if (error) {
    errorCount.current += 1;
    if (errorCount.current === 1) {
      message.error(MESSAGE_ERROR.M02);
      const timeout = setTimeout(() => {
        errorCount.current = 0;
        clearTimeout(timeout);
      }, 5000);
    }
  }

  if (!data || error) {
    return (
      <Container>
        <TourTrashPageHeader onOpenTourTrashModal={onClickBtnCreateTourTrash} />
        <SelectButtons onChangeValue={(item: string) => setCountrySelected(item)} />
        <Skeleton active />
      </Container>
    );
  }

  const tourTrashListClose = data.data
    .filter(item => !!item.product && item.status === TourTrashStatusType.CLOSED)
    .sort((a: any, b: any) => {
      return new Date(b.startDate).getTime() - new Date(a.startDate).getTime();
    });

  const tourTrashListActive = data.data
    .filter(item => !!item.product && item.status === TourTrashStatusType.ACTIVE)
    .sort((a: any, b: any) => {
      return new Date(a.startDate).getTime() - new Date(b.startDate).getTime();
    });

  const tourTrashListComingSoon = data.data
    .filter(item => !!item.product && item.status === TourTrashStatusType.COMING_SOON)
    .sort((a: any, b: any) => {
      return new Date(a.startDate).getTime() - new Date(b.startDate).getTime();
    });

  const tourTrashList = [...tourTrashListActive, ...tourTrashListComingSoon, ...tourTrashListClose].map(
    (item, index) => ({
      ...item,
      index,
    }),
  );

  const hasDraftTourTrash = draftTourtrashList?.length > 0;

  const handleChange = (item: TourTrashStatusType) => {
    if (item !== displayStatus) {
      setDisplayStatus(item);
      setPageDraft(1);
    }
  };

  const options: any[] = [
    {
      value: TourTrashStatusType.DRAFT,
      label: TourTrashStatusType.DRAFT,
    },
    {
      value: TourTrashStatusType.ARCHIVED,
      label: TourTrashStatusType.ARCHIVED,
    },
  ];

  const refreshData = () => {
    mutate();
    fetchDataDraft();
  };

  return (
    <Container>
      <TourTrashPageHeader onOpenTourTrashModal={onClickBtnCreateTourTrash} />
      <SelectButtons onChangeValue={(item: string) => setCountrySelected(item)} />
      <Content>
        <TourTrashTable
          onHandleViewTourtrash={onHandleViewTourtrash}
          records={tourTrashList}
          loading={loading || isValidating}
          onRefresh={refreshData}
          onHandleClickBtnEdit={onHandleClickBtnEdit}
          pagination={{
            showSizeChanger: true,
            onChange: handleChangePage,
            pageSize,
            current: page,
            total: data?.total,
            onShowSizeChange,
            pageSizeOptions: PAGE_SIZE_OPTIONS,
          }}
        />
        {/* {hasDraftTourTrash && <StyledTypoTitle level={4}>{t('title.tourTrashDraft')}</StyledTypoTitle>} */}
        <Select
          defaultValue={TourTrashStatusType.DRAFT}
          style={{
            width: 150,
            marginRight: 20,
            marginBottom: 20,
            marginTop: 50,
          }}
          onChange={handleChange}
          options={options}
        />
        {hasDraftTourTrash ? (
          <TourTrashTable
            records={draftTourtrashList}
            loading={isValidatingDraft}
            onHandleViewTourtrash={displayStatus !== TourTrashStatusType.DRAFT ? onHandleViewTourtrash : undefined}
            onRefresh={fetchDataDraft}
            className="draft-tourtrash-table"
            onHandleClickBtnEdit={onHandleClickBtnEdit}
            pagination={{
              showSizeChanger: true,
              onChange: handleChangePageDraft,
              pageSize: pageSizeDraft,
              current: pageDraft,
              total: dataDraft?.total,
              onShowSizeChange: onShowSizeChangeDraft,
              pageSizeOptions: PAGE_SIZE_OPTIONS,
            }}
          />
        ) : (
          <div style={{ width: '100%', height: 250 }} className="d-flex justify-content-center">
            <IconUserProfile />
          </div>
        )}
      </Content>
      {tourTrashModal}
    </Container>
  );
};

const Container = styled.div`
  ${appStyles.paddingComponent}
  .ant-table-thead {
    border-bottom: 2px solid #eee;
  }
  .ant-table-thead > tr > th {
    ${appStyles.background.tableBgGray}
    text-transform: uppercase;
    font-size: 0.75rem;
    font-weight: 600;
    &:before {
      display: none;
    }
  }
  .draft-tourtrash-table {
    .ant-table-thead {
      display: none;
    }
  }
  .ant-spin-nested-loading > div > .ant-spin {
    background: rgba(255, 255, 255, 0.7);
    max-height: 100%;
  }
  .ant-pagination-item a {
    line-height: 30px;
  }
`;
